import React, { useEffect, useState } from "react";
import { VisibilityOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { Box, Typography, Divider, CircularProgress, Chip } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import { CardLayout, EmptyState, IconButton } from "../v3";
import { AnalyticsService } from "../../services";

export const StageOverview = () => {
  const [projectStages, setProjectStages] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData({ page: 1, limit: 4 });
  }, [id]);

  const fetchData = async (projectStageFilters = {}) => {
    setLoading(true);
    try {
      const { data: projectStageSummary } = await AnalyticsService.getProjectStageSummary({
        ...projectStageFilters,
        projectIds: id,
      });
      setProjectStages(projectStageSummary.rows);
      setTotalCount(projectStageSummary.count);
    } catch (error) {
      toast.error("Failed to fetch project stages");
    } finally {
      setLoading(false);
    }
  };

  const handleViewStages = () => {
    navigate(`/projects/${id}/stages`);
  };

  return (
    <CardLayout sx={{ margin: "11px" }}>
      {/* header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#674BF5" }}>
          {`Stage Overview (${totalCount})`}
        </Typography>
        <IconButton
          icon={<VisibilityOutlined />}
          buttonText="All stages"
          tooltip="View"
          onClick={handleViewStages}
          styles={{ color: "#919191", backgroundColor: "#F2F2F2" }}
          hoverStyles={{ backgroundColor: "#ecddfdff" }}
        />
      </Box>

      {/* body */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} sx={{ color: "#674BF5" }} />
        </Box>
      ) : projectStages.length > 0 ? (
        projectStages.map((stage, index) => (
          <>
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                py: 0.5,
              }}
            >
              {/* stage name chip */}
              <Chip
                label={stage?.projectStageName}
                sx={{
                  backgroundColor: "#F5F5F5",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  borderRadius: "30px",
                  minWidth: "150px",
                  maxWidth: "150px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={stage?.projectStageName}
              />

              {/* estimated amount */}
              <Box
                sx={{
                  minWidth: 100,
                  maxWidth: 150,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  ₹ {Number(stage?.estimatedAmount)}{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: "#999",
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    est.
                  </Typography>
                </Typography>
              </Box>

              {/* balance amount */}
              <Box
                sx={{
                  minWidth: 100,
                  maxWidth: 150,
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                >
                  ₹ {Number(stage?.projectStageBalance)}{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      color: "#999",
                      fontSize: "0.8rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    bal.
                  </Typography>
                </Typography>
              </Box>
            </Box>

            {/* divider between rows */}
            {index < projectStages.length - 1 && <Divider sx={{ my: 0.5, borderColor: "#797979" }} />}
          </>
        ))
      ) : (
        <EmptyState title="No stages found" subtitle="Try adding a new stage to get started!" />
      )}
    </CardLayout>
  );
};
